import { AlertCircle, FileText } from "lucide-react";
import type { DerivedSection, DerivedSelection } from "./derive-precon-sections";
import type { PreconCover } from "./precon-types";

interface Props {
  derived: DerivedSelection;
  /** Ordered list of section plan_ids (the document's render order). */
  order: string[];
  cover: PreconCover;
}

/**
 * Read-only Review step: a "what you'll get" document outline. Built purely
 * from `derived` + `order` + `cover` — it gathers no input. Generate itself
 * lives in the wizard footer (wired in the shell).
 */
export function PreconReviewStep({ derived, order, cover }: Props) {
  const { sections, unlocated } = derived;

  // Resolve the user's order into derived sections (skip any stale id).
  const sectionById = new Map<string, DerivedSection>(
    sections.map((s) => [s.plan_id, s]),
  );
  const orderedSections = order
    .map((id) => sectionById.get(id))
    .filter((s): s is DerivedSection => s !== undefined);

  // US-007 / Edge case: no plan-scoped section means the backend can't form a
  // survey. Surface the stop here; the shell disables Generate on the same
  // condition (`derived.sections.length === 0`).
  if (sections.length === 0) {
    return (
      <section className="space-y-5">
        <h2 className="text-base font-semibold">Review</h2>
        <div className="flex items-start gap-3 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <p className="flex-1 text-sm">
            A Pre-Condition Survey needs at least one plan with photos. Go back
            and pin some of your photos to a plan, then return here to generate.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-5">
      <h2 className="text-base font-semibold">Review</h2>

      {/* "You'll get" document outline */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium">You&apos;ll get</h3>
        <ul
          aria-label="Document outline"
          className="divide-y rounded-md border"
        >
          <li className="flex items-center gap-3 px-4 py-3">
            <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="text-sm font-medium">
              Cover{cover.title ? ` — ${cover.title}` : ""}
            </span>
          </li>

          {orderedSections.map((section) => (
            <li
              key={section.plan_id}
              className="flex items-center gap-3 px-4 py-3"
            >
              <span className="text-sm font-medium">{section.planName}</span>
            </li>
          ))}

          {unlocated.length > 0 && (
            <li className="flex items-center gap-3 px-4 py-3">
              <span className="text-sm font-medium">General Observations</span>
            </li>
          )}

          <li className="flex items-center gap-3 px-4 py-3 text-muted-foreground">
            <FileText className="h-4 w-4 shrink-0" />
            <span className="text-sm">Back cover</span>
          </li>
        </ul>
      </div>

    </section>
  );
}
